import React, {useState, useMemo} from "react";
import {connect} from "react-redux";
import {useNavigate} from "react-router-dom";
import {Admission} from "@reside/reside-api-admission";

import {AppState, select} from "../../store";
import {Column, ListTable} from "../list-table";
import {AdmissionStatusCell, ResidentCell} from "../list-table/AdmissionsCells";
import {DateCell, FlagsCell, TextCell} from "../list-table/SharedCells";
import {PercentageCell} from "../list-table/PercentageCells";
import {SortDirection} from "../../store/reporting-reports/reporting-reports.common";
import {createPreflightUrl} from "../../pages/page-admin-preflight/route";
import {readDateOfAdmissionAnswer} from "../../models/AdmissionModel.helpers";
import {ActionMenuCell} from "./ActionMenuCell";
import {
  AdmissionsDeleteModalProvider,
  AdmissionsDeleteAdmissionModal,
  AdmissionsForceDeleteModal,
} from "./admission-delete-modal";
import {
  AdmissionsRestoreAdmissionModal,
  AdmissionsRestoreModalProvider,
} from "./admission-restore-modal";
import {
  ChangeHistoryModal,
  ChangeHistoryModalProvider,
} from "../dashboard-table/atoms/change-history-modal";
import {StarAdmissionIcon} from "../star-icon/StarAdmissionIcon";
import {WorkflowBadge} from "../dashboard-cell/WorkflowBadge";
import {getWorkflowStyle} from "../dashboard-cell/WorkflowRibbon";
import {WorkflowType} from "../../utils/enums";
import {isPendingWorkflowType} from "../start-workflow/workflowMapping";
import {PdfUploadStatusIcon} from "../dashboard-table/atoms/pdf-upload-status-icon/PdfUploadStatusIcon";
import {getFlagCount} from "../../services/AdmissionsService";
import {TriggeredWorkflowIcon} from "../dashboard-table/atoms/triggered-workflow-icon";

const SHOWABLE_STATUSES = [
  Admission.StatusEnum.IN_PROGRESS,
  Admission.StatusEnum.SIGNED,
  Admission.StatusEnum.APPROVED,
  Admission.StatusEnum.CANCELLED,
  Admission.StatusEnum.ARCHIVED,
];

export const AdminAdmissionsTableRenderer = ({
  admissions,
  onSort,
  sortBy,
  sortDirection,
  tableName,
  isResideAdmin,
  onAdmissionUpdated,
  onWorkflowSet,
  isAllowedToSeeAdmissions = true,
}: {
  sortBy?: string;
  sortDirection?: SortDirection;
  tableName: string;
  onSort?: (sortBy: string, sortDirection: SortDirection) => void;
  admissions: ReadonlyArray<Admission>;
  isResideAdmin: boolean;
  /**
   * Called with the admission's id after a delete or restore. Whether the row has left the list
   * depends on the list: one filtered by status loses it, "All" keeps it with its new status. On
   * the dashboard a held-back id means "hide this row until the replica agrees", so the caller
   * decides.
   */
  onAdmissionUpdated: (admissionId?: string) => void;
  onWorkflowSet?: () => void;
  isAllowedToSeeAdmissions?: boolean;
}) => {
  const [activeRowIndex, setActiveRowIndex] = useState<number>();
  const navigate = useNavigate();

  const admissionById = useMemo(
    () => new Map(admissions.map(a => [a.id, a])),
    [admissions],
  );

  return (
    <AdmissionsDeleteModalProvider>
      <AdmissionsRestoreModalProvider>
        <ChangeHistoryModalProvider>
          <AdmissionsDeleteAdmissionModal onDeleted={onAdmissionUpdated} />
          <AdmissionsForceDeleteModal onDeleted={onAdmissionUpdated} />
          <AdmissionsRestoreAdmissionModal onRestored={onAdmissionUpdated} />
          <ChangeHistoryModal />
          <ListTable<Admission>
            key={tableName}
            showDoubleArrowOnNotSorted
            sortedData={isAllowedToSeeAdmissions ? admissions : []}
            getRowId={row => row.rowData.id}
            accentBorder
            getRowProps={(row: any) => ({
              style: {
                "--row-accent-color": getWorkflowStyle(row.rowData.workflowType)
                  .color,
              } as React.CSSProperties,
            })}
            rowHeight={120}
            sortPredicates={{
              residentFullName: [
                "answers.resident_lastName.value",
                "answers.resident_firstName.value",
              ],
              code: ["code"],
              status: ["status"],
              created: ["created"],
              updated: ["updated"],
              dateOfAdmission: ["answers.preflight_dateOfAdmission.value"],
            }}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
            onRowClick={(id: string) => {
              const admission = admissionById.get(id);

              // Pending admissions have no template yet, so neither preflight nor the
              // workflow detail view can load them (even once cancelled). Actions live
              // in the row dropdown instead.
              if (!admission || isPendingWorkflowType(admission.workflowType)) {
                return;
              }

              if (SHOWABLE_STATUSES.includes(admission.status)) {
                navigate(`/admin/workflows/${admission.id}`);
              }

              if (
                admission.status === Admission.StatusEnum.PRE_FLIGHT &&
                !isResideAdmin
              ) {
                navigate(
                  createPreflightUrl({
                    facilityId: admission.facilityId,
                    admissionId: admission.id,
                    workflowType:
                      admission.workflowType || WorkflowType.ADMISSION,
                  }),
                );
              }
            }}
            noRowsMessage="No Workflows found with these parameters..."
            activeRowIndex={activeRowIndex}
          >
            <Column
              cellRenderer={(row: any) => (
                <StarAdmissionIcon
                  workflowType={row.rowData.workflowType}
                  admissionId={row.rowData.id}
                  isStarred={row.rowData.starred}
                />
              )}
              label={"star-empty"}
              sortKey="star"
              width={100}
            />
            <Column
              cellRenderer={(row: any) => (
                <WorkflowBadge workflowType={row.rowData.workflowType} />
              )}
              label="Workflow"
              sortKey="workflowType"
              width={160}
            />
            <Column
              cellRenderer={(row: any) => (
                <ResidentCell admission={row.rowData} />
              )}
              label="Resident"
              sortKey="residentFullName"
              width={350}
            />

            <Column
              cellRenderer={(row: any) => (
                <TextCell subtle data-test-id="admission-id">
                  {row.rowData.code}
                </TextCell>
              )}
              label="Workflow ID"
              sortKey="code"
              width={150}
            />
            <Column
              cellRenderer={(row: any) => (
                <AdmissionStatusCell status={row.rowData.status} />
              )}
              label="Status"
              sortKey="status"
              width={150}
            />
            <Column
              cellRenderer={(row: any) => (
                <PercentageCell data={row.rowData.completionPercentage || 0} />
              )}
              label="Completed"
              sortKey="completed"
              width={150}
            />
            <Column
              cellRenderer={(row: any) => (
                <DateCell data={readDateOfAdmissionAnswer(row.rowData)} />
              )}
              label="Date of Entry"
              sortKey="dateOfAdmission"
              width={200}
            />
            <Column
              cellRenderer={(row: any) => (
                <DateCell
                  data={row.rowData.signatureDate}
                  timeZone={row.rowData.facilityTimeZoneId}
                />
              )}
              label="Signature Date"
              sortKey="signatureDate"
              width={130}
            />
            <Column
              cellRenderer={(row: any) => (
                <DateCell data={row.rowData.created} />
              )}
              label="Date added"
              sortKey="created"
              width={130}
            />
            <Column
              cellRenderer={(row: any) => (
                <DateCell data={row.rowData.updated} />
              )}
              label="Last modified"
              sortKey="updated"
              width={130}
            />
            <Column
              cellRenderer={(row: any) => (
                <FlagsCell data={getFlagCount(row.rowData)} />
              )}
              label=""
              width={60}
            />
            <Column
              cellRenderer={(row: any) => {
                const status = row.rowData.resources?.finalPdf?.pccUploadStatus;
                return status ? (
                  <PdfUploadStatusIcon pdfUploadStatus={status as any} />
                ) : null;
              }}
              label=""
              width={60}
            />
            <Column
              cellRenderer={(row: any) =>
                row.rowData.sourceWorkflowId ? (
                  <TriggeredWorkflowIcon
                    workflowType={row.rowData.workflowType}
                  />
                ) : null
              }
              label=""
              width={60}
            />
            <Column
              cellRenderer={(row: any) => (
                <ActionMenuCell
                  data={row.rowData}
                  onOpen={() => setActiveRowIndex(row.rowIndex)}
                  onClose={() => setActiveRowIndex(undefined)}
                  onWorkflowSet={
                    onWorkflowSet ?? (() => onAdmissionUpdated())
                  }
                />
              )}
              label=""
              width={60}
            />
          </ListTable>
        </ChangeHistoryModalProvider>
      </AdmissionsRestoreModalProvider>
    </AdmissionsDeleteModalProvider>
  );
};

const mapState = (state: AppState) => ({
  isResideAdmin: select.adminSession.isResideAdmin(state),
});

export const AdminAdmissionsTable = connect(mapState)(
  AdminAdmissionsTableRenderer,
);
